import React, { useEffect, useState } from 'react';
import { fetchBgvInProgressCandidates } from '../../services/user/api';
import { Search } from 'lucide-react';
import '../../styles/user/bgvInProgress.css';
 
const BgvInProgressCandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // 1-based for UI
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
 
  useEffect(() => {
    loadCandidates(currentPage - 1); // backend 0-based
  }, [currentPage, entriesPerPage]);
 
  const loadCandidates = async (page) => {
    try {
      setIsLoading(true);
      const res = await fetchBgvInProgressCandidates(page, entriesPerPage);
      const data = res.data.data || {};

      setCandidates(data.content || []);
      setTotalElements(data.totalElements || 0);
      setFetchError('');
    } catch (err) {
      console.error(err);
      setFetchError('Failed to load BGV In Progress candidates.');
      setCandidates([]);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter only in frontend without calling API
  const filtered = candidates.filter((c) =>
    Object.values(c).some((val) =>
      String(val || '')
        .toLowerCase()
        .includes(search.toLowerCase())
    )
  );
 
  const totalPages = Math.ceil(totalElements / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
 
  return (
    <>
      <p className="client-header">BGV In Progress Candidates</p>
      <div className="client-wrapper">
        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{' '}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1); // reset to first page
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>{' '}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                id="client-search-input"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1); // ✅ fix: reset to page 1 not 0
                }}
                placeholder="Search by name"
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          {isLoading ? (
            <div className="loading">Loading BGV In Progress candidates...</div>
          ) : fetchError ? (
            <div className="error-message">{fetchError}</div>
          ) : (
            <table className="candidates-clinet-table">
              <thead>
                <tr>
                  <th>Unique ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile Number</th>
                  <th>DOB</th>
                  <th>Case Initiated Date</th>
                  <th>Case Initiated Time</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((c) => (
                    <tr key={c.candidateid}>
                      <td>{c.candidateid}</td>
                      <td>{c.fullname}</td>
                      <td>{c.email}</td>
                      <td>{c.mobileNumber}</td>
                      <td>{c.dateOfBirth}</td>
                      <td>{c.caseInitiatedDateOnly || 'N/A'}</td>
                      <td>{c.caseInitiatedTimeOnly || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="no-data">
                      No matching candidates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {!isLoading && !fetchError && totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-summary">
                Showing {startIndex + 1} to{' '}
                {Math.min(startIndex + entriesPerPage, totalElements)} of {totalElements} entries
              </div>
              <div className="pagination-buttons">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  ←
                </button>

                <button className="page-btn active">{currentPage}</button>

                {currentPage < totalPages && (
                  <button className="page-btn" onClick={() => setCurrentPage(currentPage + 1)}>
                    {currentPage + 1}
                  </button>
                )}

                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
 
export default BgvInProgressCandidatesPage;